import {
  ButtonIcon,
  Icon,
  InputField,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

interface PlainRowProps {
  label: React.ReactNode;
  value?: string | null;
  isLoading?: boolean;
  isError?: boolean;
}

export function SidePanelPlainRow({
  label,
  value,
  isLoading,
  isError,
}: PlainRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <Typography kind="body3" color="--color-foreground-neutral-subtle">
        {label}
      </Typography>
      {isLoading ? (
        <Typography kind="body2" color="--color-foreground-neutral-subtle">
          <FormattedMessage
            id="KfljRn"
            defaultMessage="Loading..."
            description="Loading message"
          />
        </Typography>
      ) : isError ? (
        <Typography kind="body2" color="--color-foreground-critical-bold">
          <FormattedMessage
            id="1s8rhN"
            defaultMessage="Failed to load credentials"
            description="Failed to load credentials error message"
          />
        </Typography>
      ) : value ? (
        <Typography
          kind="body2"
          color="--color-foreground-secondary-bold"
          className="w-11/12 break-words overflow-wrap-anywhere"
        >
          {value}
        </Typography>
      ) : (
        <Typography
          kind="body2"
          color="--color-foreground-secondary-bold"
          className="w-11/12 break-words overflow-wrap-anywhere"
        >
          <FormattedMessage
            id="J2ygci"
            defaultMessage="—"
            description="Em-dash placeholder for empty side panel field value"
          />
        </Typography>
      )}
    </div>
  );
}

interface MaskedRowProps {
  label: React.ReactNode;
  value?: string | null;
  inputName: string;
  isLoading?: boolean;
  isError?: boolean;
}

export function SidePanelMaskedRow({
  label,
  value,
  inputName,
  isLoading,
  isError,
}: MaskedRowProps) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <Typography kind="body3" color="--color-foreground-neutral-subtle">
        {label}
      </Typography>
      {isLoading ? (
        <Typography kind="body2" color="--color-foreground-neutral-subtle">
          <FormattedMessage
            id="KfljRn"
            defaultMessage="Loading..."
            description="Loading message"
          />
        </Typography>
      ) : isError ? (
        <Typography kind="body2" color="--color-foreground-critical-bold">
          <FormattedMessage
            id="1s8rhN"
            defaultMessage="Failed to load credentials"
            description="Failed to load credentials error message"
          />
        </Typography>
      ) : !value ? (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          <FormattedMessage
            id="J2ygci"
            defaultMessage="—"
            description="Em-dash placeholder for empty side panel field value"
          />
        </Typography>
      ) : (
        <div className="flex items-center gap-2">
          <InputField
            name={inputName}
            placeholder=""
            value={revealed ? value : "••••••••••••••••••"}
            backgroundColor="secondary"
            isReadOnly
          />
          <ButtonIcon
            kind="ghost"
            icon={
              <Icon
                kind={revealed ? "VisibilityOff" : "VisibilityOn"}
                size="small"
              />
            }
            ariaLabel="Toggle visibility"
            onClick={() => setRevealed((r) => !r)}
          />
        </div>
      )}
    </div>
  );
}
